import { retrospectQueryKeys } from "@/hooks/api/retrospect/queryKeys";
import { spaceQueryKeys } from "@/hooks/api/space/queryKeys";
import { createContext, useCallback, useEffect } from "react";
import { ProgressBar } from "@/component/common/ProgressBar";
import { css } from "@emotion/react";
import { useMultiStepForm } from "@/hooks/useMultiStepForm";
import { usePostRetrospectCreate } from "@/hooks/api/retrospect/create/usePostRetrospectCreate";
import { useAtom, useAtomValue } from "jotai";
import { retrospectCreateAtom } from "@/store/retrospect/retrospectCreate";
import { REQUIRED_QUESTIONS } from "@/component/retrospectCreate/customTemplate/questions.const";
import { retrospectInitialState } from "@/store/retrospect/retrospectInitial";
import { MainInfo } from "./steps/MainInfo";
import { DueDate } from "./steps/DueDate";
import { ConfirmDefaultTemplate } from "./steps/ConfirmDefaultTemplate";
import { PATHS } from "@layer/shared";
import { useNavigate } from "react-router-dom";
import { useFunnelModal } from "@/hooks/useFunnelModal";
import { useToast } from "@/hooks/useToast";
import { queryClient } from "@/lib/tanstack-query/queryClient";
import { trackEvent } from "@/lib/google-analytics";
import { GA_EVENTS, GA_FUNNEL_LABELS } from "@/lib/google-analytics/events";
import { branchLayoutAtom } from "@/store/auth/authAtom";

const PAGE_STEPS = ["confirmTemplate", "mainInfo", "dueDate"] as const;
const CUSTOM_TEMPLATE_STEPS = ["confirmDefaultTemplate", "editQuestions", "confirmEditTemplate"] as const;

type UseMultiStepFormContextState<T extends (typeof CUSTOM_TEMPLATE_STEPS)[number] | (typeof PAGE_STEPS)[number]> = ReturnType<
  typeof useMultiStepForm<T>
>;

type RetrospectCreateContextState = UseMultiStepFormContextState<(typeof PAGE_STEPS)[number]> & {
  confirmQuitPage?: () => void;
  isMutatePending: boolean;
};

export const RetrospectCreateContext = createContext<RetrospectCreateContextState>({} as RetrospectCreateContextState);

export function RetrospectCreate() {
  const navigate = useNavigate();
  const { closeFunnelModal } = useFunnelModal();
  const { toast } = useToast();
  const branchLayout = useAtomValue(branchLayoutAtom);

  const [retrospectValue, setRetrospectValue] = useAtom(retrospectInitialState);
  const spaceIdNumber = Number(retrospectValue.spaceId);
  const templateIdNumber = Number(retrospectValue.templateId);

  const retroCreateData = useAtomValue(retrospectCreateAtom);
  const { mutate: postRetrospectCreate, isPending } = usePostRetrospectCreate(spaceIdNumber);

  const pageState = useMultiStepForm({
    steps: PAGE_STEPS,
  });

  const handleSubmit = useCallback(() => {
    if (!pageState.isLastStep) return;

    const questionsWithRequired = REQUIRED_QUESTIONS.concat(retroCreateData.questions);
    postRetrospectCreate(
      {
        spaceId: spaceIdNumber,
        body: { ...retroCreateData, questions: questionsWithRequired, curFormId: templateIdNumber },
      },
      {
        onSuccess: async () => {
          // 사용자가 템플릿을 변경하거나, 회고를 생성할 때 변경된 정보가 있으면 스페이스 정보를 다시 가져옵니다.
          await queryClient.invalidateQueries({
            queryKey: spaceQueryKeys.info(spaceIdNumber),
          });
          // 사용자가 회고를 추가했다면, 회고 리스트 조회를 다시 리패치합니다.
          await queryClient.invalidateQueries({
            queryKey: retrospectQueryKeys.list(spaceIdNumber),
          });
          navigate(PATHS.spaceDetail(String(spaceIdNumber)));
          closeFunnelModal();
          toast.success("회고가 생성되었어요!");

          setRetrospectValue((prev) => ({
            ...prev,
            tempTemplateId: null,
            saveTemplateId: false,
          }));

          branchLayout === "A" ? trackEvent(GA_EVENTS.RETROSPECT.ADD_COMPLETE_A_LAYOUT) : trackEvent(GA_EVENTS.RETROSPECT.ADD_COMPLETE_B_LAYOUT);
        },
      },
    );
  }, [retroCreateData.deadline]);

  // 회고 생성 퍼널 단계 변경 시 GA 이벤트를 전송해요
  useEffect(() => {
    trackEvent({
      action: "retrospect_create_funnel_view",
      category: "retrospect",
      label: GA_FUNNEL_LABELS.RETROSPECT_CREATE[pageState.currentStep],
    });
  }, [pageState.currentStep]);

  return (
    <RetrospectCreateContext.Provider value={{ ...pageState, isMutatePending: isPending }}>
      <form
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1 1 0;
          overflow-y: auto;
        `}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ProgressBar curPage={pageState.currentStepIndex + 1} lastPage={pageState.totalStepsCnt} />
        {pageState.currentStep === "confirmTemplate" ? <ConfirmDefaultTemplate /> : null}
        {pageState.currentStep === "mainInfo" ? <MainInfo /> : null}
        {pageState.currentStep === "dueDate" ? <DueDate /> : null}
      </form>
    </RetrospectCreateContext.Provider>
  );
}
